import { useQuery, useMutation, useQueryClient, useInfiniteQuery, type InfiniteData } from '@tanstack/react-query';
import { useApiClient } from '../contexts/ApiContext';
import { queryKeys } from '../lib/queryClient';
import {
  CaseBattleControllerGetActiveCaseBattlesParams,
  CaseBattleHistoryItemResponse,
  CaseBattleLobbyMode,
  CaseBattleLobbySort,
  CreateCaseBattleRequest,
  SaveCaseBattleTemplateRequest,
} from '../services/generated/Api';
import { useAuthedInfiniteQuery, useAuthedQuery } from './useAuthedQuery';
import { isClockSynced, syncServerClock } from '../components/caseBattle/showClock';

const withServerClock = async <T extends { serverNow?: string }>(request: () => Promise<T>): Promise<T> => {
  const startedAt = Date.now();
  const response = await request();
  syncServerClock(response.serverNow, startedAt);
  return response;
};

const refetchUntilClockSynced = () => (isClockSynced() ? true : ('always' as const));

export interface LobbyFilters {
  modes: CaseBattleLobbyMode[];
  players: number | null;
  team: boolean | null;
  affordable: boolean;
  sort: CaseBattleLobbySort;
}

export const DEFAULT_LOBBY_FILTERS: LobbyFilters = {
  modes: [],
  players: null,
  team: null,
  affordable: false,
  sort: CaseBattleLobbySort.PriceDesc,
};

export const lobbyFiltersToParams = (filters: LobbyFilters): CaseBattleControllerGetActiveCaseBattlesParams => ({
  ...(filters.modes.length > 0 ? { mode: filters.modes } : {}),
  ...(filters.players !== null ? { players: filters.players } : {}),
  ...(filters.team !== null ? { team: filters.team } : {}),
  ...(filters.affordable ? { affordable: true } : {}),
  sort: filters.sort,
});

export function useLobbyCaseBattles(filters: LobbyFilters = DEFAULT_LOBBY_FILTERS, enabled = true) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: queryKeys.caseBattleLobby(filters),
    queryFn: () => withServerClock(() => apiClient.api.caseBattleControllerGetActiveCaseBattles(lobbyFiltersToParams(filters))),
    staleTime: 30 * 1000,
    refetchInterval: 30000,
    refetchOnMount: refetchUntilClockSynced,
    enabled,
  });
}

export function useCaseBattleDetails(battleId: string, enabled = true) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: queryKeys.caseBattleDetails(battleId),
    queryFn: () => withServerClock(() => apiClient.api.caseBattleControllerGetCaseBattleDetails(battleId)),
    enabled: enabled && !!battleId,
    // WHY: 30s = SSR revalidate window of the battle page; shorter and refetchOnMount: 'always' discards the hydrated cache
    // and double-fetches
    staleTime: 30 * 1000,
    refetchOnMount: refetchUntilClockSynced,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === 'PROCESSING' || status === 'IN_PROGRESS') return 2000;
      if (status === 'WAITING') return 10_000;
      return false;
    },
  });
}

export function useCaseBattleViewers(battleId: string, enabled = true) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: queryKeys.caseBattleViewers(battleId),
    queryFn: () => apiClient.api.caseBattleControllerGetViewers(battleId),
    enabled: enabled && !!battleId,
    staleTime: 15 * 1000,
  });
}

export function useCaseBattleConfig() {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: queryKeys.caseBattleConfig,
    queryFn: () => apiClient.api.caseBattleControllerGetConfig(),
    staleTime: 10 * 60 * 1000,
  });
}

export function useCaseBattleTemplates(enabled = true) {
  const apiClient = useApiClient();

  return useAuthedQuery({
    queryKey: queryKeys.caseBattleTemplates,
    queryFn: () => apiClient.api.caseBattleControllerGetTemplates(),
    staleTime: 60 * 1000,
    enabled,
  });
}

const HISTORY_PAGE_SIZE = 20;

interface HistoryPage {
  items: CaseBattleHistoryItemResponse[];
  skip: number;
  hasMore: boolean;
}

const uniqueById = <T extends { id: string }>(items: T[]): T[] => {
  const seen = new Set<string>();
  return items.filter((item) => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
};

const selectHistoryItems = (data: InfiniteData<HistoryPage, number>): CaseBattleHistoryItemResponse[] =>
  uniqueById(data.pages.flatMap((page) => page.items));

const historyPages = (
  key: readonly unknown[],
  fetchPage: (skip: number) => Promise<{ items: CaseBattleHistoryItemResponse[]; hasMore: boolean }>,
  enabled: boolean,
) => ({
  queryKey: key,
  queryFn: async ({ pageParam = 0 }): Promise<HistoryPage> => {
    const response = await fetchPage(pageParam);
    return { items: response.items, skip: pageParam, hasMore: response.hasMore };
  },
  getNextPageParam: (lastPage: HistoryPage) => (lastPage.hasMore ? lastPage.skip + HISTORY_PAGE_SIZE : undefined),
  initialPageParam: 0,
  select: selectHistoryItems,
  enabled,
});

export function useCaseBattleHistoryAllInfinite(enabled = true) {
  const apiClient = useApiClient();
  return useInfiniteQuery(
    historyPages(
      queryKeys.caseBattleHistoryAll,
      (skip) => apiClient.api.caseBattleControllerGetAllCaseBattleHistory({ skip, take: HISTORY_PAGE_SIZE }),
      enabled,
    ),
  );
}

export function useCaseBattleHistoryMineInfinite(enabled = true) {
  const apiClient = useApiClient();
  return useAuthedInfiniteQuery(
    historyPages(
      queryKeys.caseBattleHistoryMine,
      (skip) => apiClient.api.caseBattleControllerGetUserCaseBattleHistory({ skip, take: HISTORY_PAGE_SIZE }),
      enabled,
    ),
  );
}

function useInvalidateBattle() {
  const queryClient = useQueryClient();
  return (battleId?: string) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.caseBattles });
    queryClient.invalidateQueries({ queryKey: queryKeys.userActiveBattlesCount });
    queryClient.invalidateQueries({ queryKey: queryKeys.profile });
    if (battleId) {
      queryClient.invalidateQueries({ queryKey: queryKeys.caseBattleDetails(battleId) });
    }
  };
}

export function useCreateCaseBattle() {
  const apiClient = useApiClient();
  const invalidate = useInvalidateBattle();

  return useMutation({
    mutationFn: (data: CreateCaseBattleRequest) =>
      apiClient.api.caseBattleControllerCreateCaseBattle(data),
    onSuccess: () => invalidate(),
  });
}

export function useJoinCaseBattle() {
  const apiClient = useApiClient();
  const invalidate = useInvalidateBattle();

  return useMutation({
    mutationFn: ({ battleId, slot }: { battleId: string; slot?: number }) =>
      apiClient.api.caseBattleControllerJoinCaseBattle(battleId, slot === undefined ? {} : { slot }),
    onSuccess: (_data, { battleId }) => invalidate(battleId),
  });
}

export function useLeaveCaseBattle() {
  const apiClient = useApiClient();
  const invalidate = useInvalidateBattle();

  return useMutation({
    mutationFn: (battleId: string) =>
      apiClient.api.caseBattleControllerLeaveCaseBattle(battleId),
    onSuccess: (_data, battleId) => invalidate(battleId),
  });
}

export function useStartNowCaseBattle() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ battleId, startNow }: { battleId: string; startNow: boolean }) =>
      apiClient.api.caseBattleControllerStartNow(battleId, { startNow }),
    onSuccess: (_data, { battleId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.caseBattleDetails(battleId) });
    },
  });
}

export function useRematchCaseBattle() {
  const apiClient = useApiClient();
  const invalidate = useInvalidateBattle();

  return useMutation({
    mutationFn: (battleId: string) => apiClient.api.caseBattleControllerRematch(battleId),
    onSuccess: (_data, battleId) => invalidate(battleId),
  });
}

export function useRespondRematch() {
  const apiClient = useApiClient();
  const invalidate = useInvalidateBattle();

  return useMutation({
    mutationFn: ({ battleId, accept }: { battleId: string; accept: boolean }) =>
      accept
        ? apiClient.api.caseBattleControllerAcceptRematch(battleId)
        : apiClient.api.caseBattleControllerDeclineRematch(battleId),
    onSuccess: (data, { battleId }) => {
      invalidate(battleId);
      invalidate(data.rematchBattleId);
    },
  });
}

export function useSellAllCaseBattleItems() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (battleId: string) => apiClient.api.caseBattleControllerSellAll(battleId),
    onSuccess: (_data, battleId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.caseBattleDetails(battleId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.profile });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}

export function useReactCaseBattle() {
  const apiClient = useApiClient();

  return useMutation({
    mutationFn: ({ battleId, emoji }: { battleId: string; emoji: string }) =>
      apiClient.api.caseBattleControllerReact(battleId, { emoji }),
  });
}

export function useViewCaseBattle() {
  const apiClient = useApiClient();

  return useMutation({
    mutationFn: ({ battleId, leave = false }: { battleId: string; leave?: boolean }) =>
      leave ? apiClient.api.caseBattleControllerLeaveView(battleId) : apiClient.api.caseBattleControllerView(battleId),
  });
}

export function useSaveCaseBattleTemplate() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SaveCaseBattleTemplateRequest) => apiClient.api.caseBattleControllerSaveTemplate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.caseBattleTemplates });
    },
  });
}

export function useDeleteCaseBattleTemplate() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (templateId: string) => apiClient.api.caseBattleControllerDeleteTemplate(templateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.caseBattleTemplates });
    },
  });
}

export function useUserActiveBattlesCount() {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: queryKeys.userActiveBattlesCount,
    queryFn: () => apiClient.api.caseBattleControllerGetUserActiveBattlesCount(),
    staleTime: 30 * 1000,
  });
}
